import React, { useState, useEffect } from 'react';
import {
  Container,
  Table,
  Card,
  CardBody,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  Spinner,
  Badge,
  Pagination,
  PaginationItem,
  PaginationLink,
} from 'reactstrap';
import axios from 'axios';

const API_BASE_URL = 'https://api.editezy.com/api/admin';

const ManageReelCategory = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Modal states
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);

  // Form states
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryName, setCategoryName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch all categories
  const fetchCategories = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/reelcategories`);
      if (response.data.success) {
        setCategories(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Failed to load categories. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentCategories = categories.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(categories.length / itemsPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Go to previous page
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Go to next page
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Generate pagination items with ellipsis
  const renderPaginationItems = () => {
    const items = [];
    const maxVisiblePages = 5;
    const halfVisible = Math.floor(maxVisiblePages / 2);

    let startPage = Math.max(1, currentPage - halfVisible);
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Previous button
    items.push(
      <PaginationItem key="prev" disabled={currentPage === 1}>
        <PaginationLink previous onClick={goToPreviousPage} />
      </PaginationItem>
    );

    // First page
    if (startPage > 1) {
      items.push(
        <PaginationItem key={1}>
          <PaginationLink onClick={() => paginate(1)}>1</PaginationLink>
        </PaginationItem>
      );
      if (startPage > 2) {
        items.push(
          <PaginationItem key="ellipsis-start" disabled>
            <PaginationLink>...</PaginationLink>
          </PaginationItem>
        );
      }
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <PaginationItem key={i} active={currentPage === i}>
          <PaginationLink onClick={() => paginate(i)}>
            {i}
          </PaginationLink>
        </PaginationItem>
      );
    }

    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(
          <PaginationItem key="ellipsis-end" disabled>
            <PaginationLink>...</PaginationLink>
          </PaginationItem>
        );
      }
      items.push(
        <PaginationItem key={totalPages}>
          <PaginationLink onClick={() => paginate(totalPages)}>
            {totalPages}
          </PaginationLink>
        </PaginationItem>
      );
    }

    // Next button
    items.push(
      <PaginationItem key="next" disabled={currentPage === totalPages}>
        <PaginationLink next onClick={goToNextPage} />
      </PaginationItem>
    );

    return items;
  };

  // Create category
  const handleCreateCategory = async (e) => {
    e.preventDefault();

    if (!categoryName.trim()) {
      setError('Category name is required');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const response = await axios.post(`${API_BASE_URL}/createreelcategory`, {
        name: categoryName.trim()
      });

      if (response.data.success) {
        setSuccess('Category created successfully!');
        setCreateModal(false);
        resetForm();
        fetchCategories();

        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      console.error('Error creating category:', err);
      setError(err.response?.data?.message || 'Failed to create category');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Update category
  const handleUpdateCategory = async (e) => {
    e.preventDefault();

    if (!categoryName.trim()) {
      setError('Category name is required');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const response = await axios.put(
        `${API_BASE_URL}/reelcategories/${selectedCategory._id}`,
        {
          name: categoryName.trim()
        }
      );

      if (response.data.success) {
        setSuccess('Category updated successfully!');
        setEditModal(false);
        resetForm();
        fetchCategories();

        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      console.error('Error updating category:', err);
      setError(err.response?.data?.message || 'Failed to update category');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete category
  const handleDeleteCategory = async () => {
    setIsSubmitting(true);
    setError('');

    try {
      const response = await axios.delete(`${API_BASE_URL}/reelcategories/${selectedCategory._id}`);

      if (response.data.success) {
        setSuccess('Category deleted successfully!');
        setDeleteModal(false);
        fetchCategories();

        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      console.error('Error deleting category:', err);
      setError(err.response?.data?.message || 'Failed to delete category');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Open edit modal and populate form
  const openEditModal = (category) => {
    setSelectedCategory(category);
    setCategoryName(category.name);
    setEditModal(true);
  };

  // Open delete modal
  const openDeleteModal = (category) => {
    setSelectedCategory(category);
    setDeleteModal(true);
  };

  // View single category
  const handleViewCategory = async (category) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/reelcategories/${category._id}`);
      if (response.data.success) {
        setSelectedCategory(response.data.data);
        setViewModal(true);
      }
    } catch (err) {
      console.error('Error fetching category details:', err);
      setError('Failed to load category details');
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setCategoryName('');
    setSelectedCategory(null);
    setError('');
  };

  // Loading state
  if (loading) {
    return (
      <Container className="text-center my-5">
        <Spinner color="primary" />
        <p className="mt-3">Loading categories...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap">
        <h2 className="text-primary mb-2 mb-md-0">Reel Categories</h2>
        <Button
          color="primary"
          onClick={() => {
            resetForm();
            setCreateModal(true);
          }}
        >
          + Create New Category
        </Button>
      </div>

      {/* Alerts */}
      {error && (
        <Alert color="danger" toggle={() => setError('')} className="mb-3">
          {error}
        </Alert>
      )}

      {success && (
        <Alert color="success" toggle={() => setSuccess('')} className="mb-3">
          {success}
        </Alert>
      )}

      {/* Categories Table */}
      <Card className="shadow-sm">
        <CardBody>
          <div className="table-responsive">
            <Table hover bordered striped>
              <thead className="bg-light">
                <tr>
                  <th style={{ width: '5%' }}>#</th>
                  <th style={{ width: '30%' }}>Category Name</th>
                  <th style={{ width: '25%' }}>ID</th>
                  <th style={{ width: '25%' }}>Created At</th>
                  <th style={{ width: '15%' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentCategories.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center">
                      No categories found. Create your first category!
                    </td>
                  </tr>
                ) : (
                  currentCategories.map((category, index) => (
                    <tr key={category._id}>
                      <td className="align-middle">
                        {indexOfFirstItem + index + 1}
                      </td>
                      <td className="align-middle">
                        <strong>{category.name}</strong>
                      </td>
                      <td className="align-middle">
                        <code className="text-muted small">
                          {category._id}
                        </code>
                      </td>
                      <td className="align-middle">
                        <Badge color="info" pill>
                          {new Date(category.createdAt).toLocaleDateString()}
                        </Badge>
                        <br />
                        <small className="text-muted">
                          {new Date(category.createdAt).toLocaleTimeString()}
                        </small>
                      </td>
                      <td className="align-middle">
                        <div className="d-flex gap-2 flex-wrap">
                          <Button
                            size="sm"
                            color="info"
                            outline
                            onClick={() => handleViewCategory(category)}
                          >
                            View
                          </Button>
                          <Button
                            size="sm"
                            color="warning"
                            outline
                            onClick={() => openEditModal(category)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            color="danger"
                            outline
                            onClick={() => openDeleteModal(category)}
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          {/* Pagination with Ellipsis */}
          {categories.length > 0 && (
            <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap">
              <div className="text-muted small mb-2 mb-md-0">
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, categories.length)} of {categories.length} entries
              </div>
              <Pagination className="mb-0">
                {renderPaginationItems()}
              </Pagination>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Create Modal */}
      <Modal isOpen={createModal} toggle={() => setCreateModal(false)} size="md">
        <ModalHeader toggle={() => setCreateModal(false)}>
          Create New Reel Category
        </ModalHeader>
        <Form onSubmit={handleCreateCategory}>
          <ModalBody>
            <FormGroup>
              <Label for="categoryName">Category Name *</Label>
              <Input
                id="categoryName"
                type="text"
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
                placeholder="Enter category name"
                required
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setCreateModal(false)}>
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? <Spinner size="sm" /> : 'Create'}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={editModal} toggle={() => setEditModal(false)} size="md">
        <ModalHeader toggle={() => setEditModal(false)}>
          Edit Reel Category
        </ModalHeader>
        <Form onSubmit={handleUpdateCategory}>
          <ModalBody>
            <FormGroup>
              <Label for="editCategoryName">Category Name *</Label>
              <Input
                id="editCategoryName"
                type="text"
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
                placeholder="Enter category name"
                required
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setEditModal(false)}>
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? <Spinner size="sm" /> : 'Update'}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* View Modal */}
      <Modal isOpen={viewModal} toggle={() => setViewModal(false)} size="lg">
        <ModalHeader toggle={() => setViewModal(false)}>
          Category Details
        </ModalHeader>
        <ModalBody>
          {selectedCategory && (
            <div>
              <h4 className="mb-3">{selectedCategory.name}</h4>
              <hr />
              <p><strong>ID:</strong> <code>{selectedCategory._id}</code></p>
              <p><strong>Created At:</strong> {new Date(selectedCategory.createdAt).toLocaleString()}</p>
              <p><strong>Updated At:</strong> {new Date(selectedCategory.updatedAt).toLocaleString()}</p>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setViewModal(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Modal */}
      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)}>
        <ModalHeader toggle={() => setDeleteModal(false)}>
          Confirm Delete
        </ModalHeader>
        <ModalBody>
          <p>Are you sure you want to delete the category <strong>"{selectedCategory?.name}"</strong>?</p>
          <p className="text-danger mb-0"><strong>⚠️ This action cannot be undone!</strong></p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setDeleteModal(false)}>
            Cancel
          </Button>
          <Button color="danger" onClick={handleDeleteCategory} disabled={isSubmitting}>
            {isSubmitting ? <Spinner size="sm" /> : 'Delete'}
          </Button>
        </ModalFooter>
      </Modal>
    </Container>
  );
};

export default ManageReelCategory;